import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { colors } from "../../assets/colors";
import { useCursorHandlers, useSectionCursor } from "../general/Cursor";
import { useAuth } from "../../contexts/AuthContext";
import { getActiveCohorts, deleteCohort, createCohort, getBlogMessagesByCohortId, getNewsletterContent, FIREBASE_NOT_CONFIGURED_MSG } from "./backend/blogService";
import { sendNewsletterWithEmailJS, buildNewsletterHtml } from "./backend/newsletterEmail";
import trashIcon from "../../assets/trash-white.svg";

const logoContext = require.context('../../assets/logos', false, /\.png$/);
const logoMap = {};
logoContext.keys().forEach((item) => {
  const logoName = item.replace('./', '').replace('.png', '');
  logoMap[logoName] = logoContext(item);
});

const getLocalLogo = (startupName) => {
  if (!startupName) return null;
  const logoName = `${startupName.replace(/\s+/g, '')}_Logo`;
  return logoMap[logoName] || null;
};

const BlogListContainer = styled.div`
  width: 100%;
  background-color: ${colors.white};
  padding: 10vh 5vw;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const BlogListContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5vh;
  box-sizing: border-box;
  padding-top: 0;
  position: relative;
`;

const BlogListHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  position: relative;
`;

const BlogListTitle = styled.h3`
  font-family: ReadexPro, sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.black};
  margin: 0;
  text-align: center;
  grid-column: 2;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  align-items: stretch;
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const BlogCardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  width: 100%;
  max-width: 100%;
  display: block;
`;

const BlogCard = styled.div`
  background-color: ${colors.white};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: none;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
`;

const BlogCardImage = styled.div`
  height: 100px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
  flex-shrink: 0;
  overflow: hidden;
  
  img {
    width: auto;
    height: 150px;
    object-fit: contain;
  }
`;

const BlogCardContent = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  box-sizing: border-box;
  min-height: 0;
`;

const BlogCardTitle = styled.h4`
  font-family: ReadexPro, sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: ${colors.black};
  margin: 0;
  text-align: center;
  line-height: 130%;
`;

const BlogCardAuthor = styled.p`
  font-family: ReadexPro, sans-serif;
  font-size: 0.9rem;
  color: ${colors.darkGrayText};
  margin: 0;
  text-align: center;
  font-weight: 400;
`;

const BlogCardPreview = styled.p`
  font-family: ReadexPro, sans-serif;
  font-size: 1rem;
  color: ${colors.darkGrayText};
  margin: 0;
  margin-top: 1rem;
  line-height: 1.6;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 12;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Button Base Component
const ButtonBase = styled.button`
  background-color: ${colors.red};
  color: ${colors.white};
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${colors.darkGray};
    transform: translateY(-2px);
  }
`;

// Button Variations
const AddCohortButton = styled(ButtonBase)`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  text-align: center;
  min-width: 250px;
`;

const SendNewsletterButton = styled(ButtonBase)`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  text-align: center;
  min-width: 160px;
`;

const IconButton = styled(ButtonBase)`
  padding: 0.5rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0;
  
  img {
    width: 18px;
    height: 18px;
    display: block;
  }
`;


const BlogCardDeleteButton = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
`;

const EditForm = styled.div`
  background-color: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border: 2px solid ${colors.gray};
  border-radius: 10px;
  font-size: 1rem;
  font-family: "ReadexPro", sans-serif;
  box-sizing: border-box;
  width: 100%;
  text-indent: 0;
  
  &:focus {
    outline: none;
    border-color: ${colors.red};
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const CancelButton = styled(ButtonBase)`
  background-color: ${colors.gray};
  color: ${colors.black};
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  width: 120px;
  margin: 0;
  box-sizing: border-box;
  
  &:hover {
    background-color: ${colors.gray};
    color: ${colors.black};
    opacity: 0.8;
    transform: none;
  }
`;

const SaveButton = styled(ButtonBase)`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  width: 120px;
  margin: 0;
  box-sizing: border-box;
`;


// Auth Status Component
const AuthStatusContainer = styled.div`
  background-color: ${colors.white};
  border: 2px solid ${colors.gray};
  border-radius: 10px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  align-items: center;
  flex-shrink: 0;
`;

const AuthButton = styled.button`
  background-color: ${colors.red};
  color: ${colors.white};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: none;
  transition: all 0.3s ease;
  font-family: ReadexPro, sans-serif;
  align-self: flex-start;
  margin-top: 0;
  
  &:hover {
    background-color: ${colors.darkGray};
    transform: translateY(-2px);
  }
`;



const AuthInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  align-items: flex-start;
`;

const AuthTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 200px;
  min-width: 0;
`;

const AuthText = styled.p`
  font-family: ReadexPro, sans-serif;
  font-size: 0.9rem;
  color: ${colors.black};
  margin: 0;
  font-weight: 500;
  line-height: 1.2;
`;

const AuthEmail = styled.p`
  font-family: ReadexPro, sans-serif;
  font-size: 0.85rem;
  color: ${colors.darkGrayText};
  margin: 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function BlogList() {
  const location = useLocation();
  const { currentUser, signInWithGoogle, logout } = useAuth();
  const sectionCursorHandlers = useSectionCursor({ color: colors.black });
  const cursorHandlers = useCursorHandlers();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);

  // Check if user is admin (team or designated admin)
  const ADMIN_EMAILS = ['team@txmomentum.org', 'bhasin.arnav07@gmail.com'];
  const isAdmin = currentUser &&
    ADMIN_EMAILS.some((e) => currentUser.email.toLowerCase() === e.toLowerCase());
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ startupName: '', email: '', founderNames: '' });
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [newsletterPreviewing, setNewsletterPreviewing] = useState(false);

  const logoSize = 56;
  const urlToSmallDataUrl = async (url) => {
    if (!url) return null;
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = logoSize;
      canvas.height = logoSize;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, logoSize, logoSize);
      const scale = Math.min(logoSize / img.naturalWidth, logoSize / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      const x = (logoSize - w) / 2;
      const y = (logoSize - h) / 2;
      ctx.drawImage(img, x, y, w, h);
      return canvas.toDataURL('image/jpeg', 0.65);
    } catch {
      return null;
    }
  };

  const getNewsletterContentWithLogos = async () => {
    const content = await getNewsletterContent();
    const itemsWithLogos = await Promise.all(
      content.items.map(async (item) => {
        const logoUrl = getLocalLogo(item.startupName);
        const logoDataUrl = await urlToSmallDataUrl(logoUrl);
        return { ...item, logoDataUrl: logoDataUrl || null };
      })
    );
    return { ...content, items: itemsWithLogos };
  };

  const handlePreviewNewsletter = async () => {
    setNewsletterPreviewing(true);
    try {
      const contentWithLogos = await getNewsletterContentWithLogos();
      const html = buildNewsletterHtml(contentWithLogos.items);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Preview newsletter error:', err);
      alert(`Preview failed: ${err?.message || String(err)}`);
    } finally {
      setNewsletterPreviewing(false);
    }
  };

  const handleSendNewsletter = async () => {
    setNewsletterSending(true);
    try {
      const contentWithLogos = await getNewsletterContentWithLogos();
      const toEmail = 'bhasin.arnav07@gmail.com';
      await sendNewsletterWithEmailJS(contentWithLogos, toEmail);
      alert(`Newsletter sent to ${toEmail}. Check your inbox.`);
    } catch (err) {
      console.error('Send newsletter error:', err);
      const msg = err?.message || err?.text || (err?.response?.text) || (typeof err === 'object' ? JSON.stringify(err) : String(err));
      alert(`Failed to send newsletter: ${msg}`);
    } finally {
      setNewsletterSending(false);
    }
  };

  const handleSignIn = async () => {
    setAuthLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Failed to sign in. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleDeleteCohort = async (cohortId) => {
    if (!window.confirm('Are you sure you want to delete this cohort? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteCohort(cohortId);
      // Remove the deleted cohort from the list
      setBlogPosts(blogPosts.filter(post => post.id !== cohortId));
    } catch (error) {
      console.error('Error deleting cohort:', error);
      alert('Failed to delete cohort. Please try again.');
    }
  };

  const handleAddCohort = () => {
    setShowAddForm(!showAddForm);
    if (showAddForm) {
      // Reset form when closing
      setFormData({ startupName: '', email: '', founderNames: '' });
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setFormData({ startupName: '', email: '', founderNames: '' });
  };

  const handleSaveCohort = async () => {
    if (!formData.startupName.trim()) {
      alert('Please fill in the startup name.');
      return;
    }

    try {
      await createCohort({
        startupName: formData.startupName.trim(),
        email: formData.email.trim(),
        status: 'Active',
        founderNames: formData.founderNames.trim(),
      });

      // Refresh the list with previews
      const updatedCohorts = await getActiveCohorts();
      const posts = await Promise.all(
        updatedCohorts.map(async (cohort) => {
          let latestPostPreview = '';
          try {
            const messages = await getBlogMessagesByCohortId(cohort.cohortId);
            if (messages.length > 0) {
              const latestMessage = messages[0]; // Already sorted by date desc
              const content = latestMessage.messageBody || '';
              // Truncate to 2000 characters
              latestPostPreview = content.length > 2000 
                ? content.substring(0, 2000) + '...' 
                : content;
            }
          } catch (err) {
            // If fetching messages fails, just continue without preview
            console.error(`Error fetching messages for cohort ${cohort.cohortId}:`, err);
          }

          return {
            id: cohort.id,
            startupName: cohort.startupName,
            title: `${cohort.startupName}'s Blog`,
            author: cohort.founderNames,
            preview: latestPostPreview,
          };
        })
      );
      setBlogPosts(posts);

      // Reset form and close
      setFormData({ startupName: '', email: '', founderNames: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error creating cohort:', error);
      const message = error?.message || String(error);
      alert(`Failed to create cohort. ${message}`);
    }
  };

  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const cohorts = await getActiveCohorts();
        
        // Fetch the most recent blog post for each cohort
        const posts = await Promise.all(
          cohorts.map(async (cohort) => {
            let latestPostPreview = '';
            try {
              const messages = await getBlogMessagesByCohortId(cohort.cohortId);
              if (messages.length > 0) {
                const latestMessage = messages[0]; // Already sorted by date desc
                const content = latestMessage.messageBody || '';
                // Truncate to 2000 characters
                latestPostPreview = content.length > 2000 
                  ? content.substring(0, 2000) + '...' 
                  : content;
              }
            } catch (err) {
              // If fetching messages fails, just continue without preview
              console.error(`Error fetching messages for cohort ${cohort.cohortId}:`, err);
            }

            return {
              id: cohort.id,
              startupName: cohort.startupName,
              title: `${cohort.startupName}'s Blog`,
              author: cohort.founderNames,
              preview: latestPostPreview,
            };
          })
        );

        setBlogPosts(posts);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching cohorts:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCohorts();
  }, [location.pathname]);

  if (loading) {
    return (
      <BlogListContainer {...sectionCursorHandlers}>
        <BlogListContent>
          <BlogListTitle>Blogs of Current Startups</BlogListTitle>
          <div style={{ textAlign: 'center', padding: '2rem', color: colors.darkGrayText }}>
            Loading...
          </div>
        </BlogListContent>
      </BlogListContainer>
    );
  }

  if (error) {
    const isFirebaseNotConfigured = String(error).includes(FIREBASE_NOT_CONFIGURED_MSG) || String(error).includes('Firebase not configured');
    return (
      <BlogListContainer {...sectionCursorHandlers}>
        <BlogListContent>
          <BlogListTitle>Blogs of Current Startups</BlogListTitle>
          <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '480px', margin: '0 auto' }}>
            {isFirebaseNotConfigured ? (
              <>
                <div style={{ color: colors.darkGrayText, marginBottom: '1rem' }}>
                  Blog data isn’t loaded because Firebase isn’t configured on this machine.
                </div>
                <div style={{ color: colors.darkGrayText, fontSize: '0.9rem', textAlign: 'left' }}>
                  <strong>To run the blog locally:</strong>
                  <ol style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '1rem' }}>
                    <li>Copy <code>.env.example</code> to <code>.env.local</code></li>
                    <li>Get Firebase config from your team (same values used in the demo)</li>
                    <li>Fill in the <code>REACT_APP_FIREBASE_*</code> variables in <code>.env.local</code></li>
                    <li>Restart the dev server (<code>npm start</code>)</li>
                  </ol>
                </div>
              </>
            ) : (
              <div style={{ color: colors.red }}>Error loading blogs: {error}</div>
            )}
          </div>
        </BlogListContent>
      </BlogListContainer>
    );
  }

  return (
    <BlogListContainer {...sectionCursorHandlers}>
      <BlogListContent>
        <BlogListHeader>
          {isAdmin && (
            <div style={{ gridColumn: 1, justifySelf: 'start', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <AddCohortButton {...cursorHandlers} onClick={handleAddCohort}>
                {showAddForm ? 'Cancel' : '+ Add New Startup'}
              </AddCohortButton>
              <SendNewsletterButton {...cursorHandlers} onClick={handlePreviewNewsletter} disabled={newsletterPreviewing}>
                {newsletterPreviewing ? 'Opening…' : 'Preview newsletter'}
              </SendNewsletterButton>
              <SendNewsletterButton {...cursorHandlers} onClick={handleSendNewsletter} disabled={newsletterSending}>
                {newsletterSending ? 'Sending…' : 'Send newsletter'}
              </SendNewsletterButton>
            </div>
          )}
          <BlogListTitle>Blogs of Current Startups</BlogListTitle>
          <AuthStatusContainer style={{ gridColumn: 3, justifySelf: 'end' }}>
            {currentUser ? (
              <AuthInfo>
                <AuthTextContainer>
                  <AuthText>Signed in as:</AuthText>
                  <AuthEmail>{currentUser.email}</AuthEmail>
                </AuthTextContainer>
                <AuthButton {...cursorHandlers} onClick={handleSignOut}>Sign Out</AuthButton>
              </AuthInfo>
            ) : (
              <AuthInfo>
                <AuthButton {...cursorHandlers} onClick={handleSignIn} disabled={authLoading}>
                  {authLoading ? 'Signing in...' : 'Sign in with Google'}
                </AuthButton>
              </AuthInfo>
            )}
          </AuthStatusContainer>
        </BlogListHeader>
        {showAddForm && isAdmin && (
          <EditForm>
            <FormInput
              type="text"
              placeholder="Startup Name"
              value={formData.startupName}
              onChange={(e) => setFormData({ ...formData, startupName: e.target.value })}
            />
            <FormInput
              type="text"
              placeholder="Email (optional)"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <FormInput
              type="text"
              placeholder="Founder Names"
              value={formData.founderNames}
              onChange={(e) => setFormData({ ...formData, founderNames: e.target.value })}
            />
            <FormButtons>
              <CancelButton {...cursorHandlers} onClick={handleCancelAdd}>Cancel</CancelButton>
              <SaveButton {...cursorHandlers} onClick={handleSaveCohort}>Save</SaveButton>
            </FormButtons>
          </EditForm>
        )}
        <BlogGrid>
          {blogPosts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: colors.darkGrayText, gridColumn: '1 / -1' }}>
              No active startups found.
            </div>
          ) : (
            blogPosts.map((post) => (
              <div key={post.id} style={{ position: 'relative' }}>
                <BlogCardLink 
                  {...cursorHandlers}
                  to={`/blog/${encodeURIComponent(post.startupName)}`}
                >
                  <BlogCard>
                    <BlogCardImage>
                      {(() => {
                        const localLogo = getLocalLogo(post.startupName);
                        return localLogo ? (
                          <img src={localLogo} alt={post.title} />
                        ) : (
                          <div style={{ color: colors.darkGrayText }}>Logo</div>
                        );
                      })()}
                    </BlogCardImage>
                    <BlogCardContent>
                      <BlogCardTitle>{post.title}</BlogCardTitle>
                      <BlogCardAuthor>{post.author}</BlogCardAuthor>
                      {post.preview && (
                        <BlogCardPreview>{post.preview}</BlogCardPreview>
                      )}
                    </BlogCardContent>
                  </BlogCard>
                </BlogCardLink>
                {isAdmin && (
                  <BlogCardDeleteButton>
                    <IconButton 
                      {...cursorHandlers}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteCohort(post.id);
                      }}
                    >
                      <img src={trashIcon} alt="Delete" />
                    </IconButton>
                  </BlogCardDeleteButton>
                )}
              </div>
            ))
          )}
        </BlogGrid>
      </BlogListContent>
    </BlogListContainer>
  );
}

export default BlogList;
